import { calculateTextPositionsInContainer } from '../admin/cover_text_container.js'
import { CoverLayoutService, ColophonService } from '../core/print/cover_core.js'
import CoverCanvasRenderer from './cover_canvas_renderer.js'
import FontSelect from './font_select.js'

export default class CoverSelector {
  constructor(container = null) {
    // Usar window.COVER_CONFIG si está disponible
    const coverConfig = window.COVER_CONFIG || {}
    this.covers = coverConfig.covers || []
    if (!this.covers.length) {
      console.warn('CoverSelector: No covers found in COVER_CONFIG')
      return
    }

    // Elementos del DOM
    this.paletteContainer = document.getElementById('cover-background-palette')
    this.bgInput = document.getElementById('cover-background-color')
    this.fontSelect = document.getElementById('cover-typography')
    
    // Elementos SVG de la previsualización
    this.svgCoverDetails = document.getElementById('svg-cover-details')
    this.leftCoverRect = document.getElementById('cover-left-sheet-background-rect')
    this.rightCoverRect = document.getElementById('cover-right-sheet-background-rect')
    this.spineRect = document.getElementById('cover-spine-background-rect')
    // Los elementos de texto se crean dinámicamente, las referencias se actualizarán después
    this.leftCoverTitleText = null
    this.leftCoverSubtitleText = null
    this.spineText = document.getElementById('cover-spine-text')
    // La contraportada no tiene elementos de texto editables, solo el logo

    // Inputs de texto
    this.titleInput = document.getElementById('cover-title')
    this.subtitleInput = document.getElementById('cover-subtitle')
    this.spineInput = document.getElementById('cover-spine-text-input')

    // Configuración del fotobook para cálculos de tamaño y metadatos de papel
    this.fotobookConfig = window.FOTOBOOK_CONFIG || {}
    this.pageWidth = this.fotobookConfig.pageWidth || 300
    this.pageHeight = this.fotobookConfig.pageHeight || 300

    // Servicios compartidos de layout y colofón para flujos cliente/admin
    this.coverLayoutService = new CoverLayoutService()
    this.colophonService = new ColophonService()

    // Estado de colores actuales de la mini-paleta seleccionada
    this.currentTextColor = null
    this.currentFillColor = null
    // Flag para saber si el usuario ya eligió explícitamente una paleta
    this.hasUserSelectedPalette = false

    // Cover por defecto
    this.currentCoverId = coverConfig.defaultCoverId || this.covers[0]?.id

    // Flag para evitar recursividad / reentradas al recalcular texto
    this.isUpdatingText = false

    // Instancia del componente FontSelect personalizado
    this.fontSelectComponent = null

    // Inicializar
    if (this.currentCoverId) {
      this.selectCover(this.currentCoverId)
    }
    this.attachEvents()
    this.initializeCharCounters()
    this.initializeFontSelect()
    // Actualizar posición del texto del lomo después de inicializar
    setTimeout(() => {
      this.updateSpineTextPosition()
    }, 100)

    // Exponer helpers compartidos para otros módulos (por ejemplo, generadores de PDF)
    // sin acoplarlos a la implementación interna de CoverSelector.
    window.LELAB = window.LELAB || {}
    window.LELAB.coverPrint = window.LELAB.coverPrint || {}
    window.LELAB.coverPrint.buildLayoutForCurrentCover = () => this.buildPrintableLayout()
    window.LELAB.coverPrint.buildColophonPreview = () => this.buildColophonPreview()

  }

  /**
   * Construye un layout imprimible agnóstico (sin DOM) para la portada actual,
   * reutilizable tanto por el cliente final como por el administrador.
   * Los valores se basan en el tamaño de página actual del fotobook y un lomo de 1 cm.
   */
  buildPrintableLayout() {
    // Dimensiones físicas aproximadas a partir de píxeles a 300 DPI
    const dpi = 300
    const widthCm = (this.pageWidth * 2.54) / dpi
    const heightCm = (this.pageHeight * 2.54) / dpi
    const spineWidthCm = 1.0 // 1 cm de lomo fijo, coherente con Package#spine_width_px

    return this.coverLayoutService.buildLayout({
      pageWidthCm: widthCm,
      pageHeightCm: heightCm,
      spineWidthCm,
      bleedCm: 1,
      dpi,
    })
  }

  /**
   * Construye el string de colofón usando las reglas de negocio compartidas.
   * Para el cliente final se usa la fecha actual como aproximación del cierre de la orden.
   */
  buildColophonPreview() {
    const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId)) || this.covers[0]
    const paperName = this.fotobookConfig.paperName || 'Papel estándar'
    const fontFamilies = cover?.font_families || 'Tipografías del sistema'

    return this.colophonService.generate({
      createdAt: new Date(),
      tipoPapel: paperName,
      tipografias: fontFamilies,
    })
  }

  initializeFontSelect() {
    // Inicializar el componente FontSelect personalizado
    if (this.fontSelect) {
      this.fontSelectComponent = new FontSelect(this.fontSelect)
    }
  }

  attachEvents() {
    // Event listeners para inputs de texto
    if (this.titleInput) {
      this.titleInput.addEventListener('input', () => {
        this.updateTitlePreview(this.titleInput.value)
      })
    }

    if (this.subtitleInput) {
      this.subtitleInput.addEventListener('input', () => {
        this.updateSubtitlePreview(this.subtitleInput.value)
      })
    }

    if (this.spineInput) {
      this.spineInput.addEventListener('input', () => {
        this.updateSpinePreview(this.spineInput.value)
      })
    }

    // Event listeners para paleta de colores
    if (this.paletteContainer) {
      this.paletteContainer.addEventListener('click', (e) => {
        const chip = e.target.closest('.palette-color-btn-fotobook, [data-background], [data-color]')
        if (chip) {
          this.handlePaletteClick(chip, e)
        }
      })
      
      setTimeout(() => {
        const firstColor = document.querySelector('.palette-color-btn-fotobook, [data-background], [data-color]')
        if (firstColor) {
          firstColor.click()
        }
      }, 500);
    }

    // Event listener para cambio de tipografía
    // El evento change se dispara desde FontSelect cuando se selecciona una opción
    if (this.fontSelect) {
      this.fontSelect.addEventListener('change', () => {
        this.updateTypography()
      })
    }

    // Botón de generar PDF
    const generatePdfButton = document.querySelector('.js-generate-cover-pdf-user')
    if (generatePdfButton) {
      generatePdfButton.addEventListener('click', () => {
        this.generateCoverPDF()
      })
    }
  }

  initializeCharCounters() {
    const countCharInputs = document.querySelectorAll('[data-char-count]')
    countCharInputs.forEach((input) => {
      this.updateCharCounter(input)
    })
  }

  selectCover(coverId, options = {}) {
    const { notifyFotobookEditor = true } = options

    // Resetear selección manual de paleta al cambiar de cover para aplicar el nuevo default (más oscuro)
    this.hasUserSelectedPalette = false

    const cover = this.covers.find(c => String(c.id) === String(coverId)) || this.covers[0]
    if (!cover) return
    this.currentCoverId = cover.id

    // Actualizar paleta y fuentes
    this.updatePalette(cover)
    this.updateFonts(cover)

    // NO actualizar los inputs - mantener los valores del usuario
    // Solo actualizar contadores si los inputs existen
    if (this.titleInput) {
      this.updateCharCounter(this.titleInput)
    }
    if (this.subtitleInput) {
      this.updateCharCounter(this.subtitleInput)
    }
    if (this.spineInput) {
      this.updateCharCounter(this.spineInput)
    }

    // Actualizar previsualización completa del cover
    // La previsualización usará los valores de los inputs si existen, sino los valores por defecto
    this.updateCoverPreview(cover)
    
    // Actualizar posición del texto del lomo y sincronizar el valor del input
    if (this.spineInput && this.spineText) {
      // Si el input tiene un valor, actualizar el SVG
      if (this.spineInput.value) {
        this.updateSpinePreview(this.spineInput.value)
      } else {
        // Si no, solo actualizar la posición
        this.updateSpineTextPosition()
      }
    } else {
      // Asegurarse de que las referencias existan
      this.spineText = document.getElementById('cover-spine-text')
      if (this.spineText) {
        this.updateSpineTextPosition()
      }
    }

    // Notificar a FotobookEditor si existe y si está permitido
    if (notifyFotobookEditor && window.fotobookEditor && window.fotobookEditor.selectCoverFormat) {
      // Buscar el elemento del formato de cover correspondiente
      const coverFormatOption = document.querySelector(`[data-cover-id="${coverId}"].cover-format-option`)
      if (coverFormatOption) {
        window.fotobookEditor.selectCoverFormat(coverFormatOption)
      }
    }
  }

  updatePalette(cover) {
    let colors = this.parseColors(cover.palette)
    if (colors.length === 0) return

    // Ordenar colores por luminancia (del más oscuro al más claro)
    colors = colors.sort((a, b) => {
      return this.getLuminance(a) - this.getLuminance(b)
    })

    // Renderizar chips de paleta en el contenedor
    if (this.paletteContainer) {
      this.paletteContainer.innerHTML = ''
      colors.forEach((color, index) => {
        const chip = document.createElement('button')
        chip.className = 'palette-color-btn-fotobook'
        chip.setAttribute('data-background', color)
        chip.setAttribute('type', 'button')
        chip.setAttribute('aria-label', `Color ${index + 1}`)
        
        const span = document.createElement('span')
        span.style.backgroundColor = color
        chip.appendChild(span)
        
        // Marcar como activo si es el color actual
        if (this.bgInput && this.bgInput.value === color) {
          chip.classList.add('active')
        }
        
        this.paletteContainer.appendChild(chip)
      })
    }

    // Si el usuario no ha seleccionado una paleta manualmente, aplicar la primera (la más oscura)
    if (!this.hasUserSelectedPalette) {
      this.setBackgroundColor(colors[0])
      // El color de texto se establece automáticamente cuando se actualiza el cover
    }
  }

  getLuminance(hex) {
    // Convertir hex a RGB
    const rgb = parseInt(hex.slice(1), 16)
    const r = (rgb >> 16) & 0xff
    const g = (rgb >> 8) & 0xff
    const b = (rgb >> 0) & 0xff

    // Calcular luminancia (fórmula estándar percibida)
    // 0.299*R + 0.587*G + 0.114*B
    return 0.299 * r + 0.587 * g + 0.114 * b
  }

  updateFonts(cover) {
    if (!this.fontSelect || !cover.font_families) return

    const fonts = cover.font_families.split(',').map(f => f.trim())
    if (fonts.length > 0) {
      // Buscar la fuente en el select o usar la primera disponible
      const fontOption = Array.from(this.fontSelect.options).find(
        opt => fonts.some(f => opt.text.toLowerCase().includes(f.toLowerCase()))
      )
      if (fontOption) {
        this.fontSelect.value = fontOption.value
        // Actualizar el componente FontSelect si existe
        if (this.fontSelectComponent) {
          this.fontSelectComponent.updateOptions()
        }
        // Actualizar el texto con la nueva tipografía
        this.updateTypography()
      }
    }
  }

  handlePaletteClick(chip, event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const paletteContainer = chip.closest('.palette-container-fotobook')
    if (!paletteContainer) return

    // Remover selección previa
    paletteContainer.querySelectorAll('.palette-color-btn-fotobook, [data-background], [data-color]').forEach(btn => {
      btn.classList.remove('active')
    })

    // Marcar como seleccionado
    chip.classList.add('active')
    this.hasUserSelectedPalette = true

    // Aplicar color de fondo
    if (chip.dataset.background) {
      this.setBackgroundColor(chip.dataset.background)
    } else if (chip.dataset.color) {
      // Si es un color de texto, actualizar el color de texto
      this.setTextColor(chip.dataset.color)
    } else {
      // Intentar obtener el color del estilo inline o del span interno
      const colorSpan = chip.querySelector('span[style*="background"]')
      if (colorSpan) {
        const bgMatch = colorSpan.style.background.match(/#[0-9a-f]{6}/i)
        if (bgMatch) {
          this.setBackgroundColor(bgMatch[0])
        }
      }
    }
  }

  parseColors(value) {
    if (!value) return []
    return value.split(',').map(c => c.trim()).filter(Boolean)
  }

  setBackgroundColor(color) {
    if (!color) return

    // Actualizar input si existe
    if (this.bgInput) {
      this.bgInput.value = color
    }

    // Actualizar estado activo en los chips de paleta
    if (this.paletteContainer) {
      this.paletteContainer.querySelectorAll('.palette-color-btn-fotobook, [data-background]').forEach(chip => {
        chip.classList.remove('active')
        if (chip.getAttribute('data-background') === color) {
          chip.classList.add('active')
        }
      })
    }

    // Actualizar rectángulos de fondo en el SVG
    const rects = [
      this.leftCoverRect,
      this.rightCoverRect,
      this.spineRect
    ].filter(Boolean)

    rects.forEach(rect => {
      if (rect) {
        rect.setAttribute('fill', color)
      }
    })

    // Guardar color actual
    this.currentFillColor = color
  }

  setTextColor(color) {
    if (!color) return

    // Guardar color actual
    this.currentTextColor = color

    // Actualizar textos en el SVG
    if (this.leftCoverTitleText) {
      this.leftCoverTitleText.setAttribute('fill', color)
    }
    // No actualizar el subtítulo, tiene su propio color (#FFFFFF)

    // Actualizar todos los textos del título en el grupo
    const titleGroup = document.getElementById('cover-left-sheet-title-group')
    if (titleGroup) {
      titleGroup.querySelectorAll('text').forEach(text => {
        text.setAttribute('fill', color)
      })
    }

    // El subtítulo mantiene su color propio (#FFFFFF), no se actualiza con setTextColor
  }

  updateTypography() {
    // Obtener la tipografía seleccionada
    const selectedFont = this.fontSelect?.value
    if (!selectedFont) return

    this.fontSelect.style.fontFamily = selectedFont;

    // Recrear el texto completo para asegurar que se aplica correctamente con el nuevo tamaño
    // Esto es necesario porque cambiar la tipografía puede cambiar el tamaño del texto
    const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
    if (cover) {
      const coverGroup = this.svgCoverDetails?.querySelector('#cover-left-sheet-group')
      const contentGroup = coverGroup?.querySelector('#cover-content-group')
      if (contentGroup) {
        // Recrear los elementos de texto con la nueva tipografía
        // ensureTextElements ya limpia los grupos anteriores antes de crear nuevos
        this.ensureTextElements(contentGroup, cover)
      }
      
      // Actualizar también el texto del lomo con la nueva tipografía
      this.updateSpineTextPosition()
    }
  }

  updateCharCounter(input) {
    const maxLength = parseInt(input.dataset.charCount) || 0
    if (maxLength === 0) return

    const counter = document.querySelector(`[data-counter-for="${input.id}"]`)
    if (!counter) return

    const currentLength = input.value.length
    counter.textContent = `${currentLength} / ${maxLength}`
    counter.classList.toggle('text-red-500', currentLength > maxLength)
  }

  updateCoverPreview(cover) {
    // Renderizar toda la estructura del cover (textos y fotos) en un solo grupo
    // Esto asegura que todo el contenido esté junto y con coordenadas limpias
    if (this.svgCoverDetails) {
      this.renderCoverDetails(cover)
    }

    // Usar valores de los inputs del usuario si existen, sino usar valores del cover
    // NO renderizar texto default - solo renderizar si hay texto real
    const titleText = (this.titleInput && this.titleInput.value.trim()) || cover.title || ''
    const subtitleText = (this.subtitleInput && this.subtitleInput.value.trim()) || cover.subtitle || ''

    // Actualizar título y subtítulo con los valores correctos
    this.updateTitlePreview(titleText)
    this.updateSubtitlePreview(subtitleText)

    // Actualizar color de fondo si hay paleta y el usuario NO ha elegido una paleta manualmente
    if (cover.palette && !this.hasUserSelectedPalette) {
      const colors = this.parseColors(cover.palette)
      if (colors.length > 0) {
        this.setBackgroundColor(colors[0])
      }
    }

    // Actualizar posiciones y tamaños del texto según el cover
    // (Los textos ya se crearon con las posiciones correctas en renderCoverDetails)
    this.updateTextPositions(cover)
  }

  renderCoverDetails(cover) {
    if (!this.svgCoverDetails) return

    // Buscar el grupo de la portada (cover-left-sheet-group) que ahora está a la derecha
    const coverGroup = this.svgCoverDetails.querySelector('#cover-left-sheet-group')
    if (!coverGroup) return

    // Obtener el grupo de contenido único dentro del grupo de la portada
    let contentGroup = coverGroup.querySelector('#cover-content-group')
    if (!contentGroup) {
      contentGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      contentGroup.setAttribute('class', 'cover-sheet-content')
      contentGroup.setAttribute('id', 'cover-content-group')
      coverGroup.appendChild(contentGroup)
    } else {
      // Limpiar todo el contenido previo (formas y textos) para renderizar desde cero
      contentGroup.innerHTML = ''
      // Resetear referencias a elementos de texto
      this.leftCoverTitleText = null
      this.leftCoverSubtitleText = null
    }

    // Primero renderizar layout de fotos
    if (cover.data) {
      this.renderCoverLayout(contentGroup, cover.data)
    }

    // Luego crear los elementos de texto, para que queden siempre por encima
    this.ensureTextElements(contentGroup, cover)
  }

  ensureTextElements(contentGroup, cover = null) {
    // Eliminar grupos de texto existentes antes de crear nuevos
    // Buscar tanto en el documento completo como dentro del contentGroup
    const existingTitleGroup = document.getElementById('cover-left-sheet-title-group') || 
                               contentGroup.querySelector('#cover-left-sheet-title-group')
    if (existingTitleGroup && existingTitleGroup.parentNode) {
      existingTitleGroup.parentNode.removeChild(existingTitleGroup)
    }
    
    const existingSubtitleGroup = document.getElementById('cover-left-sheet-subtitle-group') || 
                                  contentGroup.querySelector('#cover-left-sheet-subtitle-group')
    if (existingSubtitleGroup && existingSubtitleGroup.parentNode) {
      existingSubtitleGroup.parentNode.removeChild(existingSubtitleGroup)
    }
    
    // También eliminar cualquier grupo de texto que pueda estar dentro del contentGroup
    // (por si acaso hay elementos huérfanos)
    const allTitleGroups = contentGroup.querySelectorAll('#cover-left-sheet-title-group, g[id="cover-left-sheet-title-group"]')
    allTitleGroups.forEach(group => {
      if (group.parentNode) {
        group.parentNode.removeChild(group)
      }
    })
    
    const allSubtitleGroups = contentGroup.querySelectorAll('#cover-left-sheet-subtitle-group, g[id="cover-left-sheet-subtitle-group"]')
    allSubtitleGroups.forEach(group => {
      if (group.parentNode) {
        group.parentNode.removeChild(group)
      }
    })
    
    // Resetear referencias a elementos de texto
    this.leftCoverTitleText = null
    this.leftCoverSubtitleText = null
    
    // Obtener las dimensiones de la portada individual desde el rectángulo de fondo
    const coverGroup = contentGroup.closest('#cover-left-sheet-group')
    if (!coverGroup) return
    
    const coverRect = coverGroup.querySelector('#cover-left-sheet-background-rect')
    if (!coverRect) return
    
    // Las dimensiones de la portada individual (no del SVG completo)
    const coverWidth = parseFloat(coverRect.getAttribute('width'))
    const coverHeight = parseFloat(coverRect.getAttribute('height'))

    // Usar valores de los inputs del usuario si existen, sino usar valores del cover
    // NO renderizar texto default - solo renderizar si hay texto real
    const titleText = (this.titleInput && this.titleInput.value.trim()) || cover?.title || ''
    const subtitleText = (this.subtitleInput && this.subtitleInput.value.trim()) || cover?.subtitle || ''
    
    // Valores del sistema de contenedor (nuevo sistema)
    const containerX = cover?.text_container_x ?? 0.1
    const containerY = cover?.text_container_y ?? 0.1
    const containerWidth = cover?.text_container_width ?? 0.8
    const containerHeight = cover?.text_container_height ?? 0.3
    const containerAlign = cover?.text_container_align || 'center-center'
    const titleWrap = cover?.title_wrap ?? false
    const subtitleWrap = cover?.subtitle_wrap ?? false
    
    // Tamaños de fuente relativos (0-1)
    const titleSizeRelative = cover?.title_size ?? 0.08
    const subtitleSizeRelative = cover?.subtitle_size ?? 0.06
    
    // Convertir tamaños relativos a píxeles (usando ancho para mantener proporción)
    const titleFontSize = titleSizeRelative * coverWidth
    const subtitleFontSize = subtitleSizeRelative * coverWidth

    // Obtener la fuente del select si está disponible, sino del cover
    const selectedFont = this.fontSelect?.value
    const fontFamily = selectedFont
      ? `'${selectedFont}', sans-serif`
      : (cover?.font_families 
          ? `'${cover.font_families.split(',')[0].trim()}', sans-serif`
          : 'Arial, sans-serif')

    // Obtener el SVG para pasar a calculateTextPositionsInContainer
    const svg = contentGroup.closest('svg') || this.svgCoverDetails

    // Calcular posiciones usando el sistema de contenedor
    const positions = calculateTextPositionsInContainer({
      containerX,
      containerY,
      containerWidth,
      containerHeight,
      containerAlign,
      svgWidth: coverWidth,
      svgHeight: coverHeight,
      titleFontSize,
      subtitleFontSize,
      titleText,
      subtitleText,
      titleAutoFit: !titleWrap,
      titleWrap,
      subtitleAutoFit: !subtitleWrap,
      subtitleWrap,
      fontFamily,
      titleFontWeight: 'bold',
      subtitleFontWeight: 'normal',
      svg: svg
    })

    // Crear elemento de título (puede tener múltiples líneas)
    const titleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    titleGroup.setAttribute('id', 'cover-left-sheet-title-group')
    
    positions.title.lines.forEach((line, index) => {
      const lineY = positions.title.y + ((index - (positions.title.lines.length - 1) / 2) * positions.title.lineHeight)
      const titleLineElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      titleLineElement.setAttribute('x', positions.title.x)
      titleLineElement.setAttribute('y', lineY)
      titleLineElement.setAttribute('text-anchor', positions.title.textAnchor)
      titleLineElement.setAttribute('dominant-baseline', positions.title.dominantBaseline)
      titleLineElement.setAttribute('font-size', positions.title.fontSize)
      titleLineElement.setAttribute('fill', this.currentTextColor || '#FFF')
      titleLineElement.setAttribute('font-weight', 'bold')
      titleLineElement.setAttribute('font-family', fontFamily)
      titleLineElement.textContent = line
      titleGroup.appendChild(titleLineElement)
    })
    
    contentGroup.appendChild(titleGroup)
    // Guardar referencia al primer elemento de texto del título para compatibilidad
    this.leftCoverTitleText = titleGroup.querySelector('text') || titleGroup.firstElementChild

    // Crear elemento de subtítulo (puede tener múltiples líneas)
    const subtitleGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    subtitleGroup.setAttribute('id', 'cover-left-sheet-subtitle-group')
    
    positions.subtitle.lines.forEach((line, index) => {
      const lineY = positions.subtitle.y + ((index - (positions.subtitle.lines.length - 1) / 2) * positions.subtitle.lineHeight)
      const subtitleLineElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      subtitleLineElement.setAttribute('x', positions.subtitle.x)
      subtitleLineElement.setAttribute('y', lineY)
      subtitleLineElement.setAttribute('text-anchor', positions.subtitle.textAnchor)
      subtitleLineElement.setAttribute('dominant-baseline', positions.subtitle.dominantBaseline)
      subtitleLineElement.setAttribute('font-size', positions.subtitle.fontSize)
      subtitleLineElement.setAttribute('fill', '#FFFFFF')
      subtitleLineElement.setAttribute('font-family', fontFamily)
      subtitleLineElement.textContent = line
      subtitleGroup.appendChild(subtitleLineElement)
    })
    
    contentGroup.appendChild(subtitleGroup)
    // Guardar referencia al primer elemento de texto del subtítulo para compatibilidad
    this.leftCoverSubtitleText = subtitleGroup.querySelector('text') || subtitleGroup.firstElementChild
  }

  renderCoverLayout(contentGroup, data) {
    if (!data || !this.svgCoverDetails) return

    // Obtener las dimensiones de la portada individual desde el rectángulo de fondo
    const coverGroup = contentGroup.closest('#cover-left-sheet-group')
    if (!coverGroup) return
    
    const coverRect = coverGroup.querySelector('#cover-left-sheet-background-rect')
    if (!coverRect) return
    
    // Las dimensiones de la portada individual (no del SVG completo)
    // Estas dimensiones están en el sistema de coordenadas del viewBox del SVG
    const coverWidth = parseFloat(coverRect.getAttribute('width'))
    const coverHeight = parseFloat(coverRect.getAttribute('height'))
    
    // Verificar que las dimensiones son válidas
    if (isNaN(coverWidth) || isNaN(coverHeight) || coverWidth <= 0 || coverHeight <= 0) {
      console.error('CoverSelector: Invalid cover dimensions', { coverWidth, coverHeight })
      return
    }
    
    const shapes = data.split('|')

    shapes.forEach((shapeData, index) => {
      const shapeMatch = shapeData.trim().match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
      if (!shapeMatch) return

      // Formato: r:width,height,top,left (valores relativos 0-1)
      const [, type, widthRel, heightRel, topRel, leftRel] = shapeMatch
      const slotGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      slotGroup.setAttribute('class', 'photo-slot cover-photo-slot')
      slotGroup.setAttribute('data-slot-index', index)

      const shapeElement = type === 'r' 
        ? document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        : document.createElementNS('http://www.w3.org/2000/svg', 'circle')

      // Convertir valores relativos (0-1) a coordenadas absolutas del viewBox
      const width = parseFloat(widthRel) * coverWidth
      const height = parseFloat(heightRel) * coverHeight
      const left = parseFloat(leftRel) * coverWidth
      const top = parseFloat(topRel) * coverHeight
      const fontSize = height * 0.1

      if (type === 'r') {
        // Rectángulo: x,y son la esquina superior izquierda
        shapeElement.setAttribute('x', left)
        shapeElement.setAttribute('y', top)
        shapeElement.setAttribute('width', width)
        shapeElement.setAttribute('height', height)
      } else {
        // Para círculos, el width y height representan el diámetro
        const radius = width / 2 // Usar width como diámetro
        shapeElement.setAttribute('r', radius)
        shapeElement.setAttribute('cx', left + radius)
        shapeElement.setAttribute('cy', top + radius)
      }

      shapeElement.setAttribute('fill', 'none')
      shapeElement.setAttribute('stroke', '#55859a')
      shapeElement.setAttribute('stroke-width', '1')
      shapeElement.setAttribute('class', 'photo-slot-rect cover-photo-slot')
      shapeElement.setAttribute('data-slot-index', index)

      // Agregar imagen placeholder
      const placeholderUrl = `https://picsum.photos/${Math.round(width/3)}/${Math.round(height/3)}?random=${index}`;
      const imgElement = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      imgElement.setAttributeNS('http://www.w3.org/1999/xlink', 'href', placeholderUrl);
      imgElement.setAttribute('x', left);
      imgElement.setAttribute('y', top);
      imgElement.setAttribute('width', width);
      imgElement.setAttribute('height', height);
      imgElement.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      imgElement.setAttribute('opacity', '0.3');
      imgElement.setAttribute('class', 'photo-placeholder');

      if (type === 'c') {
        // Para círculos, agregar clip-path
        const clipId = `clip-circle-${index}`;
        let defs = this.svgCoverDetails.querySelector('defs');
        if (!defs) {
          defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
          this.svgCoverDetails.insertBefore(defs, this.svgCoverDetails.firstChild);
        }
        const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
        clipPath.setAttribute('id', clipId);
        const clipCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        const radius = width / 2;
        clipCircle.setAttribute('cx', left + radius);
        clipCircle.setAttribute('cy', top + radius);
        clipCircle.setAttribute('r', radius);
        clipPath.appendChild(clipCircle);
        defs.appendChild(clipPath);
        imgElement.setAttribute('clip-path', `url(#${clipId})`);
      }

      slotGroup.appendChild(imgElement);

      slotGroup.appendChild(shapeElement);

      contentGroup.appendChild(slotGroup)
    })
  }

  updateTitlePreview(text) {
    // Evitar re-entradas que pueden causar bucles infinitos por cambios en el SVG
    if (this.isUpdatingText) return
    this.isUpdatingText = true

    const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
    if (!cover) {
      this.isUpdatingText = false
      return
    }

    // Buscar el grupo de título
    const titleGroup = document.getElementById('cover-left-sheet-title-group')
    if (!titleGroup) {
      // Si no existe el grupo, recrear los elementos
      const coverGroup = this.svgCoverDetails?.querySelector('#cover-left-sheet-group')
      const contentGroup = coverGroup?.querySelector('#cover-content-group')
      if (contentGroup) {
        // Actualizar el texto en el cover temporalmente para recrear
        const tempCover = { ...cover, title: text || '' }
        this.ensureTextElements(contentGroup, tempCover)
        this.isUpdatingText = false
        return
      }
      this.isUpdatingText = false
      return
    }

    // Actualizar el texto en el cover temporalmente
    const tempCover = { ...cover, title: text || '' }
    
    // Obtener las dimensiones de la portada individual
    const coverGroup = this.svgCoverDetails?.querySelector('#cover-left-sheet-group')
    const coverRect = coverGroup?.querySelector('#cover-left-sheet-background-rect')
    if (!coverRect) {
      this.isUpdatingText = false
      return
    }
    
    const coverWidth = parseFloat(coverRect.getAttribute('width'))
    const coverHeight = parseFloat(coverRect.getAttribute('height'))
    
    // Recalcular posiciones usando el sistema de contenedor
    const containerX = tempCover.text_container_x ?? 0.1
    const containerY = tempCover.text_container_y ?? 0.1
    const containerWidth = tempCover.text_container_width ?? 0.8
    const containerHeight = tempCover.text_container_height ?? 0.3
    const containerAlign = tempCover.text_container_align || 'center-center'
    const titleWrap = tempCover.title_wrap ?? false
    const titleSizeRelative = tempCover.title_size ?? 0.08
    const titleFontSize = titleSizeRelative * coverWidth
    const subtitleSizeRelative = tempCover.subtitle_size ?? 0.06
    const subtitleFontSize = subtitleSizeRelative * coverWidth
    
    const subtitleText = (this.subtitleInput && this.subtitleInput.value.trim()) || tempCover.subtitle || ''
    // Obtener la fuente del select si está disponible, sino del cover
    const selectedFont = this.fontSelect?.value
    const fontFamily = selectedFont
      ? `'${selectedFont}', sans-serif`
      : (tempCover.font_families 
          ? `'${tempCover.font_families.split(',')[0].trim()}', sans-serif`
          : 'Arial, sans-serif')

    const svg = this.svgCoverDetails

    const positions = calculateTextPositionsInContainer({
      containerX,
      containerY,
      containerWidth,
      containerHeight,
      containerAlign,
      svgWidth: coverWidth,
      svgHeight: coverHeight,
      titleFontSize,
      subtitleFontSize,
      titleText: text || '',
      subtitleText,
      titleAutoFit: !titleWrap,
      titleWrap,
      subtitleAutoFit: !(tempCover.subtitle_wrap ?? false),
      subtitleWrap: tempCover.subtitle_wrap ?? false,
      fontFamily,
      titleFontWeight: 'bold',
      subtitleFontWeight: 'normal',
      svg: svg
    })

    // Limpiar y recrear líneas del título
    titleGroup.innerHTML = ''
    positions.title.lines.forEach((line, index) => {
      const lineY = positions.title.y + ((index - (positions.title.lines.length - 1) / 2) * positions.title.lineHeight)
      const titleLineElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      titleLineElement.setAttribute('x', positions.title.x)
      titleLineElement.setAttribute('y', lineY)
      titleLineElement.setAttribute('text-anchor', positions.title.textAnchor)
      titleLineElement.setAttribute('dominant-baseline', positions.title.dominantBaseline)
      titleLineElement.setAttribute('font-size', positions.title.fontSize)
      titleLineElement.setAttribute('fill', this.currentTextColor || '#FFF')
      titleLineElement.setAttribute('font-weight', 'bold')
      titleLineElement.setAttribute('font-family', fontFamily)
      titleLineElement.textContent = line
      titleGroup.appendChild(titleLineElement)
    })

    // Actualizar referencia
    this.leftCoverTitleText = titleGroup.querySelector('text') || titleGroup.firstElementChild
    this.isUpdatingText = false
  }

  updateSubtitlePreview(text) {
    // Evitar re-entradas que pueden causar bucles infinitos por cambios en el SVG
    if (this.isUpdatingText) return
    this.isUpdatingText = true

    const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
    if (!cover) {
      this.isUpdatingText = false
      return
    }

    // Buscar el grupo de subtítulo
    const subtitleGroup = document.getElementById('cover-left-sheet-subtitle-group')
    if (!subtitleGroup) {
      // Si no existe el grupo, recrear los elementos
      const coverGroup = this.svgCoverDetails?.querySelector('#cover-left-sheet-group')
      const contentGroup = coverGroup?.querySelector('#cover-content-group')
      if (contentGroup) {
        // Actualizar el texto en el cover temporalmente para recrear
        const tempCover = { ...cover, subtitle: text || '' }
        this.ensureTextElements(contentGroup, tempCover)
        this.isUpdatingText = false
        return
      }
      this.isUpdatingText = false
      return
    }

    // Actualizar el texto en el cover temporalmente
    const tempCover = { ...cover, subtitle: text || '' }
    
    // Obtener las dimensiones de la portada individual
    const coverGroup = this.svgCoverDetails?.querySelector('#cover-left-sheet-group')
    const coverRect = coverGroup?.querySelector('#cover-left-sheet-background-rect')
    if (!coverRect) {
      this.isUpdatingText = false
      return
    }
    
    const coverWidth = parseFloat(coverRect.getAttribute('width'))
    const coverHeight = parseFloat(coverRect.getAttribute('height'))
    
    // Recalcular posiciones usando el sistema de contenedor
    const containerX = tempCover.text_container_x ?? 0.1
    const containerY = tempCover.text_container_y ?? 0.1
    const containerWidth = tempCover.text_container_width ?? 0.8
    const containerHeight = tempCover.text_container_height ?? 0.3
    const containerAlign = tempCover.text_container_align || 'center-center'
    const subtitleWrap = tempCover.subtitle_wrap ?? false
    const titleSizeRelative = tempCover.title_size ?? 0.08
    const titleFontSize = titleSizeRelative * coverWidth
    const subtitleSizeRelative = tempCover.subtitle_size ?? 0.06
    const subtitleFontSize = subtitleSizeRelative * coverWidth
    
    const titleText = (this.titleInput && this.titleInput.value.trim()) || tempCover.title || ''
    // Obtener la fuente del select si está disponible, sino del cover
    const selectedFont = this.fontSelect?.value
    const fontFamily = selectedFont
      ? `'${selectedFont}', sans-serif`
      : (tempCover.font_families 
          ? `'${tempCover.font_families.split(',')[0].trim()}', sans-serif`
          : 'Arial, sans-serif')

    const svg = this.svgCoverDetails

    const positions = calculateTextPositionsInContainer({
      containerX,
      containerY,
      containerWidth,
      containerHeight,
      containerAlign,
      svgWidth: coverWidth,
      svgHeight: coverHeight,
      titleFontSize,
      subtitleFontSize,
      titleText,
      subtitleText: text || '',
      titleAutoFit: !(tempCover.title_wrap ?? false),
      titleWrap: tempCover.title_wrap ?? false,
      subtitleAutoFit: !subtitleWrap,
      subtitleWrap,
      fontFamily,
      titleFontWeight: 'bold',
      subtitleFontWeight: 'normal',
      svg: svg
    })

    // Limpiar y recrear líneas del subtítulo
    subtitleGroup.innerHTML = ''
    positions.subtitle.lines.forEach((line, index) => {
      const lineY = positions.subtitle.y + ((index - (positions.subtitle.lines.length - 1) / 2) * positions.subtitle.lineHeight)
      const subtitleLineElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      subtitleLineElement.setAttribute('x', positions.subtitle.x)
      subtitleLineElement.setAttribute('y', lineY)
      subtitleLineElement.setAttribute('text-anchor', positions.subtitle.textAnchor)
      subtitleLineElement.setAttribute('dominant-baseline', positions.subtitle.dominantBaseline)
      subtitleLineElement.setAttribute('font-size', positions.subtitle.fontSize)
      subtitleLineElement.setAttribute('fill', '#FFFFFF')
      subtitleLineElement.setAttribute('font-family', fontFamily)
      subtitleLineElement.textContent = line
      subtitleGroup.appendChild(subtitleLineElement)
    })

    // Actualizar referencia
    this.leftCoverSubtitleText = subtitleGroup.querySelector('text') || subtitleGroup.firstElementChild
    this.isUpdatingText = false
  }

  updateTextPositions(cover) {
    // Este método se puede usar para actualizar posiciones si cambian las dimensiones
    // Por ahora, las posiciones se calculan automáticamente en ensureTextElements
  }

  updateSpinePreview(text) {
    if (!this.spineText) {
      this.spineText = document.getElementById('cover-spine-text')
    }
    if (this.spineText) {
      this.spineText.textContent = text || 'Texto de lomo'
      this.updateSpineTextPosition()
    }
  }

  updateSpineTextPosition() {
    if (!this.spineText) {
      this.spineText = document.getElementById('cover-spine-text')
    }
    if (!this.spineText) return

    // Obtener el grupo del lomo
    const spineGroup = this.svgCoverDetails?.querySelector('#cover-spine-sheet-group')
    if (!spineGroup) return

    const spineRect = spineGroup.querySelector('#cover-spine-background-rect')
    if (!spineRect) return

    // Obtener dimensiones del lomo
    const spineWidth = parseFloat(spineRect.getAttribute('width'))
    const spineHeight = parseFloat(spineRect.getAttribute('height'))

    // Posición X: centro del lomo (las coordenadas son relativas al grupo cover-spine-sheet-group)
    const spineTextX = spineWidth / 2
    // Posición Y: centro vertical del lomo (igual que en el PDF, para centrar antes de rotar)
    const spineTextY = spineHeight / 2
    // Tamaño de fuente: 67.5% del ancho del lomo (igual que en Package#spine_font_size)
    const spineFontSize = spineWidth * 0.675

    // Obtener la tipografía seleccionada
    const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId))
    const selectedFont = this.fontSelect?.value
    const fontFamily = selectedFont
      ? `'${selectedFont}', sans-serif`
      : (cover?.font_families 
          ? `'${cover.font_families.split(',')[0].trim()}', sans-serif`
          : 'Arial, sans-serif')

    // Actualizar atributos del texto
    this.spineText.setAttribute('x', spineTextX)
    this.spineText.setAttribute('y', spineTextY)
    this.spineText.setAttribute('font-size', spineFontSize)
    this.spineText.setAttribute('font-family', fontFamily)
    // Cambiar dominant-baseline a "middle" para centrar el texto verticalmente
    this.spineText.setAttribute('dominant-baseline', 'middle')

    // Actualizar transform para rotación (el texto debe rotarse -90 grados desde el centro)
    // El punto de rotación es el centro del lomo, igual que en el PDF
    this.spineText.setAttribute('transform', `rotate(-90 ${spineTextX} ${spineTextY})`)
  }

  applyPlaceholderFill(fillColor) {
    // Aplicar color de relleno a los placeholders de fotos
    document.querySelectorAll('.cover-photo-slot rect, .cover-photo-slot circle').forEach(
      slot => {
        slot.setAttribute('stroke', fillColor)
      }
    )
  }

  /**
   * Genera un PDF de portada usando exactamente las mismas dimensiones y lógica del SVG del frontend
   * Replica el diseño de '.cover-sheet-container' con los cálculos correctos de impresión
   */
  async generateCoverPDF() {
    const button = document.querySelector('.js-generate-cover-pdf-user')
    if (!button) return

    // Mostrar estado de carga
    const originalText = button.innerHTML
    button.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Generando...'
    button.disabled = true

    try {
      // Importar jsPDF dinámicamente
      const { jsPDF } = await import('jspdf')
      const { calculateTextPositionsInContainer } = await import('../admin/cover_text_container.js')

      // Obtener el SVG del frontend para usar sus dimensiones exactas
      if (!this.svgCoverDetails) {
        throw new Error('No se encontró el SVG de portada')
      }

      // Obtener rectángulos del SVG para usar sus dimensiones exactas
      const backRect = this.svgCoverDetails.querySelector('#cover-right-sheet-background-rect')
      const spineRect = this.svgCoverDetails.querySelector('#cover-spine-background-rect')
      const frontRect = this.svgCoverDetails.querySelector('#cover-left-sheet-background-rect')
      
      if (!backRect || !spineRect || !frontRect) {
        throw new Error('No se encontraron los elementos del SVG')
      }

      // Usar las dimensiones exactas del SVG
      const actualCoverWidthPx = parseFloat(backRect.getAttribute('width'))
      const actualCoverHeightPx = parseFloat(backRect.getAttribute('height'))
      const actualSpineWidthPx = parseFloat(spineRect.getAttribute('width'))
      // Calcular el ancho total usando las dimensiones reales del SVG
      const actualTotalWidthPx = actualCoverWidthPx + actualSpineWidthPx + actualCoverWidthPx
      console.log("actualTotalWidthPx", actualTotalWidthPx)
      // Conversión de píxeles a mm (300 DPI)
      const dpi = 300
      const pxToMm = (px) => (px * 25.4) / dpi
      const cmToMm = (cm) => cm * 10
      
      // Dimensiones en mm del SVG (sin bleed)
      const coverWidthMm = pxToMm(actualCoverWidthPx)
      const coverHeightMm = pxToMm(actualCoverHeightPx)
      const spineWidthMm = pxToMm(actualSpineWidthPx)
      const totalWidthMm = pxToMm(actualTotalWidthPx)

      // Agregar bleed de 1cm (10mm) a cada lado para la portada exterior
      // Igual que en el admin: bleed.left + bleed.right = 1cm + 1cm = 2cm = 20mm
      // bleed.top + bleed.bottom = 1cm + 1cm = 2cm = 20mm
      const bleedCm = 1
      const bleedMm = cmToMm(bleedCm)
      const totalWidthWithBleedMm = totalWidthMm + (bleedMm * 2) // bleed izquierdo + derecho
      const totalHeightWithBleedMm = coverHeightMm + (bleedMm * 2) // bleed superior + inferior

      // Tamaño fijo del papel (33x47.5cm = 330x475mm) - NUNCA cambiar
      const paperWidthMm = 330
      const paperHeightMm = 475

      // Calcular escala para que el contenido con bleed quepa en el papel
      // El contenido siempre se dibuja en su orientación original (horizontal)
      // Calcular escala basándose en el tamaño completo del papel (sin reservar márgenes)
      const scaleX = paperWidthMm / totalWidthWithBleedMm
      const scaleY = paperHeightMm / totalHeightWithBleedMm
      const scale = Math.min(scaleX, scaleY) // Usar la escala más pequeña para que quepa en ambas dimensiones

      // Dimensiones escaladas del contenido (portada completa: contraportada + lomo + portada + bleed)
      const scaledWidth = totalWidthWithBleedMm * scale
      const scaledHeight = totalHeightWithBleedMm * scale

      // Centrar el contenido en el papel
      // offsetX = (tamaño de ancho de hoja - (ancho de portada escalado incluyendo lomo y las 2 páginas)) / 2
      const offsetX = (paperWidthMm - scaledWidth) / 2
      const offsetY = (paperHeightMm - scaledHeight) / 2

      // Verificar que el offset esté calculado correctamente para centrar
      // offsetX debe ser >= 0 (no puede ser negativo, si es negativo, escala demasiado)
      // Si el contenido escalado es más grande que el papel, el offset será negativo
      // En ese caso, deberíamos usar escala más pequeña, pero eso ya está controlado por Math.min
      
      // Calcular el offset del bleed escalado
      // contentOffsetX/Y son las coordenadas donde empieza el contenido sin bleed
      const scaledBleedMm = bleedMm * scale
      const contentOffsetX = offsetX + scaledBleedMm
      const contentOffsetY = offsetY + scaledBleedMm

      // Debug: Verificar cálculos de centrado
      // offsetXCalculated debe ser igual a: (tamaño de ancho de hoja - (ancho de portada escalado incluyendo lomo y las 2 páginas)) / 2
      const offsetXCalculated = (paperWidthMm - scaledWidth) / 2
      const offsetYCalculated = (paperHeightMm - scaledHeight) / 2
      
      console.log('PDF Cover Generation Debug:', {
        paperSize: { width: paperWidthMm, height: paperHeightMm },
        contentSize: { 
          width: totalWidthWithBleedMm, // contraportada + lomo + portada + bleed
          height: totalHeightWithBleedMm,
          totalWidthWithoutBleed: totalWidthMm // contraportada + lomo + portada (sin bleed)
        },
        scaledSize: { width: scaledWidth, height: scaledHeight },
        scale,
        scaleX,
        scaleY,
        offset: { x: offsetX, y: offsetY },
        offsetCalculated: { x: offsetXCalculated, y: offsetYCalculated },
        contentOffset: { x: contentOffsetX, y: contentOffsetY },
        scaledBleed: scaledBleedMm,
        // Verificar que offsetX es igual a offsetXCalculated
        offsetXMatch: Math.abs(offsetX - offsetXCalculated) < 0.01
      })

      // Obtener datos del cover actual
      const cover = this.covers.find(c => String(c.id) === String(this.currentCoverId)) || this.covers[0]
      if (!cover) {
        throw new Error('No se encontró el cover')
      }

      // Obtener valores de los inputs del usuario o usar valores por defecto del cover
      const titleText = (this.titleInput && this.titleInput.value.trim()) || cover.title || ''
      const subtitleText = (this.subtitleInput && this.subtitleInput.value.trim()) || cover.subtitle || ''
      const spineText = (this.spineInput && this.spineInput.value.trim()) || 'Texto de lomo'

      // Obtener color de fondo seleccionado
      const bgColorHex = this.bgInput?.value || this.parseColors(cover.palette)[0] || '#FFFFFF'
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
        return result
          ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
          : [255, 255, 255]
      }
      const bgColor = hexToRgb(bgColorHex)

      // Obtener color de texto
      const textColor = this.currentTextColor || '#FFFFFF'
      const textColorRgb = hexToRgb(textColor)

      // Obtener posiciones exactas del SVG (igual que en el frontend)
      const backCoverX = 0
      const spineX = actualCoverWidthPx
      const frontCoverX = actualCoverWidthPx + actualSpineWidthPx

      // Crear PDF con tamaño fijo de papel (SIEMPRE 330x475mm en landscape)
      // NUNCA cambiar el tamaño del papel, solo ajustar escala y posición
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [paperWidthMm, paperHeightMm],
      })

      // Fondo blanco del papel
      pdf.setFillColor(255, 255, 255)
      pdf.rect(0, 0, paperWidthMm, paperHeightMm, 'F')

      // Función auxiliar para convertir coordenadas con escala y offset
      // Las coordenadas xMm/yMm son relativas al contenido sin bleed (donde x=0 es el borde izquierdo del contenido)
      // Para convertir a coordenadas del PDF: escalamos y sumamos el offset de centrado más el bleed
      // Usamos directamente offsetX + scaledBleedMm en lugar de contentOffsetX para ser más explícitos
      const scaledX = (xMm) => {
        // xMm es la coordenada en mm del contenido (relativa al contenido sin bleed, empezando en 0)
        // Multiplicamos por escala, sumamos el offset de centrado (offsetX) y el bleed escalado
        const result = (xMm * scale) + offsetX + scaledBleedMm
        return result
      }
      const scaledY = (yMm) => {
        // yMm es la coordenada en mm del contenido (relativa al contenido sin bleed, empezando en 0)
        // Multiplicamos por escala, sumamos el offset de centrado (offsetY) y el bleed escalado
        const result = (yMm * scale) + offsetY + scaledBleedMm
        return result
      }
      const scaledW = (wMm) => wMm * scale
      const scaledH = (hMm) => hMm * scale

      // Extender el color de fondo hasta las marcas de corte (hasta el bleed completo)
      // Dibujar un rectángulo grande que cubra todo el área con bleed
      pdf.setFillColor(...bgColor)
      pdf.rect(offsetX, offsetY, scaledWidth, scaledHeight, 'F')

      // Dibujar rectángulos de fondo (igual que en el SVG, con escala)
      // Contraportada (izquierda)
      pdf.setDrawColor(85, 133, 154)
      pdf.setLineWidth(pxToMm(2) * scale) // stroke-width="2" del SVG, escalado
      pdf.setFillColor(...bgColor)
      pdf.rect(scaledX(pxToMm(backCoverX)), scaledY(0), scaledW(coverWidthMm), scaledH(coverHeightMm), 'FD')

      // Lomo (centro)
      pdf.rect(scaledX(pxToMm(spineX)), scaledY(0), scaledW(spineWidthMm), scaledH(coverHeightMm), 'FD')

      // Portada (derecha)
      pdf.rect(scaledX(pxToMm(frontCoverX)), scaledY(0), scaledW(coverWidthMm), scaledH(coverHeightMm), 'FD')

      // Dibujar "Printed in México" en contraportada (a la misma altura que el footer del lomo)
      const printedInImage = await this.loadAndConvertLogo('/printed-in.svg', 58.4, 31.92)
      if (printedInImage) {
        // Margen pequeño de 0.2cm = 2mm a 300 DPI
        const marginSmallPx = (0.2 * dpi) / 2.54 // 0.2cm en píxeles a 300 DPI
        const printedInWidthCm = 4 // 5cm de ancho
        const printedInWidthPx = (printedInWidthCm * dpi) / 2.54 // Convertir 5cm a píxeles a 300 DPI
        // Aspect ratio del SVG: 58.4 / 31.92 = 1.83 (horizontal)
        const printedInAspectRatio = 58.4 / 31.92
        const printedInHeightPx = printedInWidthPx / printedInAspectRatio
        const printedInX = (actualCoverWidthPx - printedInWidthPx) / 2 // Centrado
        // Calcular la posición Y del footer del lomo para alinear
        const spineLogoBottomWidthPx = actualSpineWidthPx * 0.6 // 80% del ancho del lomo
        const spineLogoBottomAspectRatio = 31.9 / 9.96
        const spineLogoBottomHeightPx = spineLogoBottomWidthPx * spineLogoBottomAspectRatio
        const spineLogoBottomY = actualCoverHeightPx - (actualCoverHeightPx * 0.05) - spineLogoBottomHeightPx
        // Usar la misma posición Y que el footer del lomo
        const printedInY = spineLogoBottomY
        pdf.addImage(
          printedInImage,
          'PNG',
          scaledX(pxToMm(printedInX)),
          scaledY(pxToMm(printedInY)),
          scaledW(pxToMm(printedInWidthPx)),
          scaledH(pxToMm(printedInHeightPx))
        )
      }

      // Renderizar texto del lomo en canvas a 300 DPI y añadirlo como imagen PNG
      const spineTextImage = await this.renderSpineTextToCanvas(
        spineText,
        actualSpineWidthPx,
        actualCoverHeightPx,
        cover
      )
      
      if (spineTextImage) {
        // La imagen del canvas tiene el texto ya rotado y centrado
        // Necesitamos posicionarla correctamente en el área del lomo
        const spineImageWidthMm = pxToMm(actualSpineWidthPx)
        const spineImageHeightMm = pxToMm(actualCoverHeightPx)
        
        // Añadir la imagen del texto del lomo al PDF
        // El texto ya está rotado y centrado en el canvas
        // Posicionamos la imagen en el área del lomo
        pdf.addImage(
          spineTextImage,
          'PNG',
          scaledX(pxToMm(spineX)),
          scaledY(0),
          scaledW(spineImageWidthMm),
          scaledH(spineImageHeightMm)
        )
      }

      // Logo del lomo (parte superior con 5% de margen, usando /static/lelab-logo.svg) - Cuadrado 687x687
      const spineLogoTopImage = await this.loadAndConvertLogo('/static/lelab-logo.svg', 687, 687)
      if (spineLogoTopImage) {
        const logoWidthPx = actualSpineWidthPx * 0.9 // 90% del ancho del lomo
        const logoHeightPx = logoWidthPx // Cuadrado
        const logoX = (actualSpineWidthPx - logoWidthPx) / 2 // Centrado
        const logoY = actualCoverHeightPx * 0.05 // Parte superior con 5% de margen
        pdf.addImage(
          spineLogoTopImage,
          'PNG',
          scaledX(pxToMm(spineX + logoX)),
          scaledY(pxToMm(logoY)),
          scaledW(pxToMm(logoWidthPx)),
          scaledH(pxToMm(logoHeightPx))
        )
      }

      // Logo del lomo (parte inferior con 5% de margen, usando /lelab-logo.svg) - Vertical 9.96x31.9
      const spineLogoBottomImage = await this.loadAndConvertLogo('/lelab-logo.svg', 9.96, 31.9)
      if (spineLogoBottomImage) {
        const logoWidthPx = actualSpineWidthPx * 0.5 // 80% del ancho del lomo
        // Aspect ratio del SVG: 31.9 / 9.96 = 3.2 (vertical)
        const logoAspectRatio = 31.9 / 9.96
        const logoHeightPx = logoWidthPx * logoAspectRatio
        const logoX = (actualSpineWidthPx - logoWidthPx) / 2 // Centrado
        // Parte inferior: altura total - margen del 5% desde abajo - altura del logo (mismo aire que arriba)
        const logoY = actualCoverHeightPx - (actualCoverHeightPx * 0.05) - logoHeightPx
        pdf.addImage(
          spineLogoBottomImage,
          'PNG',
          scaledX(pxToMm(spineX + logoX)),
          scaledY(pxToMm(logoY)),
          scaledW(pxToMm(logoWidthPx)),
          scaledH(pxToMm(logoHeightPx))
        )
      }

      // Renderizar toda la portada (fondo, imágenes con máscaras, textos) en un canvas y exportar como JPG
      const renderer = new CoverCanvasRenderer({ dpi: 300, jpgQuality: 1 })
      
      // Obtener color de fondo
      const coverGroup = this.svgCoverDetails.querySelector('#cover-left-sheet-group')
      const coverRect = coverGroup?.querySelector('#cover-left-sheet-background-rect')
      const backgroundColor = coverRect?.getAttribute('fill') || '#ffffff'

      // Preparar slots de fotos con sus imágenes y máscaras
      const photoSlots = []
      if (cover.data) {
        const shapes = cover.data.split('|')
        for (let index = 0; index < shapes.length; index++) {
          const shapeData = shapes[index]
          const shapeMatch = shapeData.trim().match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
          if (!shapeMatch) continue

          // Formato: r:width,height,top,left (valores relativos 0-1)
          const [, type, widthRel, heightRel, topRel, leftRel] = shapeMatch
          
          // Los valores son relativos (0-1), multiplicar por las dimensiones de la portada
          const shapeLeft = parseFloat(leftRel) * actualCoverWidthPx
          const shapeTop = parseFloat(topRel) * actualCoverHeightPx
          const shapeW = parseFloat(widthRel) * actualCoverWidthPx
          const shapeH = parseFloat(heightRel) * actualCoverHeightPx

          // Buscar si hay una imagen en este slot en el SVG
          const slotGroup = this.svgCoverDetails.querySelector(`g.cover-photo-slot[data-slot-index="${index}"]`) ||
                           this.svgCoverDetails.querySelector(`g.photo-slot[data-slot-index="${index}"]`)
          const slotImage = slotGroup?.querySelector('image')
          const slotRect = slotGroup?.querySelector('rect') || slotGroup?.querySelector('circle')
          
          if (slotImage) {
            const imageHref = slotImage.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                             slotImage.getAttribute('href') ||
                             slotImage.getAttribute('xlink:href')
            
            if (imageHref) {
              // Obtener las coordenadas del slot relativas al grupo de la portada
              // El slot está dentro de cover-left-sheet-group que tiene transform="translate(frontCoverX, 0)"
              // Pero en el canvas solo renderizamos la portada, así que las coordenadas deben ser relativas a (0,0)
              // Las coordenadas calculadas (shapeLeft, shapeTop) ya son relativas a la portada, así que las usamos directamente
              let actualSlotX = shapeLeft
              let actualSlotY = shapeTop
              
              // Verificar si el slotRect tiene coordenadas diferentes (por si hay alguna transformación)
              if (slotRect) {
                const coverGroup = slotGroup?.closest('#cover-left-sheet-group')
                if (coverGroup) {
                  // Obtener la transformación del grupo (si existe)
                  const transform = coverGroup.getAttribute('transform') || ''
                  const translateMatch = transform.match(/translate\(([^,]+),\s*([^)]+)\)/)
                  
                  if (slotRect.tagName === 'rect') {
                    const rectX = parseFloat(slotRect.getAttribute('x') || 0)
                    const rectY = parseFloat(slotRect.getAttribute('y') || 0)
                    // Si hay transformación, las coordenadas del rect ya están en el sistema del grupo
                    // que es relativo a la portada, así que las usamos directamente
                    actualSlotX = rectX
                    actualSlotY = rectY
                  } else if (slotRect.tagName === 'circle') {
                    const cx = parseFloat(slotRect.getAttribute('cx') || 0)
                    const cy = parseFloat(slotRect.getAttribute('cy') || 0)
                    const r = parseFloat(slotRect.getAttribute('r') || 0)
                    actualSlotX = cx - r
                    actualSlotY = cy - r
                  }
                }
              }
              
              // Obtener clipPath si existe
              let clipPathElement = null
              const clipPathAttr = slotImage.getAttribute('clip-path')
              if (clipPathAttr) {
                clipPathElement = renderer.getClipPathElement(this.svgCoverDetails, clipPathAttr)
              }

              photoSlots.push({
                type,
                x: actualSlotX,
                y: actualSlotY,
                width: shapeW,
                height: shapeH,
                imageUrl: imageHref,
                clipPath: clipPathElement
              })
            }
          }
        }
      }

      // Preparar configuración de textos
      let titleConfig = null
      let subtitleConfig = null
      
      if (titleText || subtitleText) {
        const containerX = cover.text_container_x ?? 0.1
        const containerY = cover.text_container_y ?? 0.1
        const containerWidth = cover.text_container_width ?? 0.8
        const containerHeight = cover.text_container_height ?? 0.3
        const containerAlign = cover.text_container_align || 'center-center'
        const titleWrap = cover.title_wrap ?? false
        const subtitleWrap = cover.subtitle_wrap ?? false
        const titleSizeRelative = cover.title_size ?? 0.08
        const subtitleSizeRelative = cover.subtitle_size ?? 0.06
        
        // Calcular tamaños de fuente en píxeles (300 DPI)
        const titleFontSizePx = titleSizeRelative * actualCoverWidthPx
        const subtitleFontSizePx = subtitleSizeRelative * actualCoverWidthPx

        // Usar la tipografía del select si está disponible, sino del cover
        const selectedFont = this.fontSelect?.value
        const fontFamily = selectedFont || (cover.font_families?.split(',')[0]?.trim()) || 'Arial'

        // Crear un SVG temporal para medir texto
        const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        tempSvg.setAttribute('width', actualCoverWidthPx)
        tempSvg.setAttribute('height', actualCoverHeightPx)
        document.body.appendChild(tempSvg)

        const positions = calculateTextPositionsInContainer({
          containerX,
          containerY,
          containerWidth,
          containerHeight,
          containerAlign,
          svgWidth: actualCoverWidthPx,
          svgHeight: actualCoverHeightPx,
          titleFontSize: titleFontSizePx,
          subtitleFontSize: subtitleFontSizePx,
          titleText,
          subtitleText,
          titleAutoFit: !titleWrap,
          titleWrap,
          subtitleAutoFit: !subtitleWrap,
          subtitleWrap,
          fontFamily: `'${fontFamily}', sans-serif`,
          titleFontWeight: 'bold',
          subtitleFontWeight: 'normal',
          svg: tempSvg,
        })

        document.body.removeChild(tempSvg)

        // Configurar título
        if (titleText && positions.title) {
          titleConfig = {
            text: titleText,
            x: positions.title.x,
            y: positions.title.y,
            fontSize: positions.title.fontSize,
            fontFamily,
            fontWeight: 'bold',
            color: this.currentTextColor || '#FFFFFF',
            textAlign: positions.title.textAnchor || 'center',
            textBaseline: positions.title.dominantBaseline || 'middle',
            lines: positions.title.lines || [titleText]
          }
        }

        // Configurar subtítulo
        if (subtitleText && positions.subtitle) {
          subtitleConfig = {
            text: subtitleText,
            x: positions.subtitle.x,
            y: positions.subtitle.y,
            fontSize: positions.subtitle.fontSize,
            fontFamily,
            fontWeight: 'normal',
            color: '#FFFFFF',
            textAlign: positions.subtitle.textAnchor || 'center',
            textBaseline: positions.subtitle.dominantBaseline || 'middle',
            lines: positions.subtitle.lines || [subtitleText]
          }
        }
      }

      // Renderizar toda la portada en canvas y exportar como JPG
      try {
        const coverImageDataUrl = await renderer.renderAndExport({
          widthPx: actualCoverWidthPx,
          heightPx: actualCoverHeightPx,
          backgroundColor,
          photoSlots,
          titleConfig,
          subtitleConfig
        })

        // Agregar la imagen completa de la portada al PDF
        pdf.addImage(
          coverImageDataUrl,
          'JPEG',
          scaledX(pxToMm(frontCoverX)),
          scaledY(0),
          scaledW(coverWidthMm),
          scaledH(coverHeightMm)
        )
      } catch (error) {
        console.error('Error rendering cover to canvas:', error)
        // Fallback: dibujar solo los contornos si falla el renderizado
        if (cover.data) {
          const shapes = cover.data.split('|')
          for (let index = 0; index < shapes.length; index++) {
            const shapeData = shapes[index]
            const shapeMatch = shapeData.trim().match(/^([rc]):([\d.]+),([\d.]+),([\d.]+),([\d.]+)$/)
            if (!shapeMatch) continue

            const [, type, widthRel, heightRel, topRel, leftRel] = shapeMatch
            const shapeLeft = parseFloat(leftRel) * actualCoverWidthPx
            const shapeTop = parseFloat(topRel) * actualCoverHeightPx
            const shapeW = parseFloat(widthRel) * actualCoverWidthPx
            const shapeH = parseFloat(heightRel) * actualCoverHeightPx
            const finalX = frontCoverX + shapeLeft
            const finalY = shapeTop

            pdf.setDrawColor(85, 133, 154)
            pdf.setLineWidth(pxToMm(1) * scale)
            if (type === 'r') {
              pdf.rect(scaledX(pxToMm(finalX)), scaledY(pxToMm(finalY)), scaledW(pxToMm(shapeW)), scaledH(pxToMm(shapeH)))
            } else if (type === 'c') {
              const radius = shapeW / 2
              pdf.ellipse(
                scaledX(pxToMm(finalX + radius)),
                scaledY(pxToMm(finalY + radius)),
                scaledW(pxToMm(radius)),
                scaledH(pxToMm(radius))
              )
            }
          }
        }
      }

      // Añadir 4 guías de corte formando un rectángulo alrededor de todo el contenido
      // Las guías deben estar en los bordes del área con bleed (offsetX, offsetY)
      const cutMarkLengthMm = 5 // Longitud de las marcas de corte en mm
      pdf.setDrawColor(0, 0, 0) // Negro para las marcas de corte
      pdf.setLineWidth(0.1) // Línea fina

      // Las coordenadas de las esquinas del área con bleed (ya centradas)
      const cutXLeft = offsetX
      const cutXRight = offsetX + scaledWidth
      const cutYTop = offsetY
      const cutYBottom = offsetY + scaledHeight

      // Función auxiliar para dibujar una marca de corte
      const drawCutMark = (xMm, yMm, direction) => {
        let x2Mm, y2Mm

        // La longitud de la marca en mm (no escalada, es fija)
        const markLengthMm = cutMarkLengthMm

        switch (direction) {
          case 'down': // Marca hacia abajo (desde esquina superior)
            x2Mm = xMm
            y2Mm = yMm + markLengthMm
            break
          case 'up': // Marca hacia arriba (desde esquina inferior)
            x2Mm = xMm
            y2Mm = yMm - markLengthMm
            break
          case 'right': // Marca hacia derecha (desde esquina izquierda)
            x2Mm = xMm + markLengthMm
            y2Mm = yMm
            break
          case 'left': // Marca hacia izquierda (desde esquina derecha)
            x2Mm = xMm - markLengthMm
            y2Mm = yMm
            break
        }
        pdf.line(xMm, yMm, x2Mm, y2Mm)
      }

      // 1. Esquina superior izquierda (left-top): marcas hacia dentro (abajo y derecha)
      drawCutMark(cutXLeft, cutYTop, 'down')
      drawCutMark(cutXLeft, cutYTop, 'right')

      // 2. Esquina superior derecha (top-right): marcas hacia dentro (abajo e izquierda)
      drawCutMark(cutXRight, cutYTop, 'down')
      drawCutMark(cutXRight, cutYTop, 'left')

      // 3. Esquina inferior izquierda (bottom-left): marcas hacia dentro (arriba y derecha)
      drawCutMark(cutXLeft, cutYBottom, 'up')
      drawCutMark(cutXLeft, cutYBottom, 'right')

      // 4. Esquina inferior derecha (bottom-right): marcas hacia dentro (arriba e izquierda)
      drawCutMark(cutXRight, cutYBottom, 'up')
      drawCutMark(cutXRight, cutYBottom, 'left')

      // Solo generar 1 página con la portada exterior (sin página de interiores)
      const widthCm = (actualCoverWidthPx * 2.54) / dpi
      const heightCm = (actualCoverHeightPx * 2.54) / dpi
      const fileName = `portada-fotobook-${widthCm.toFixed(1)}x${heightCm.toFixed(1)}cm.pdf`
      pdf.save(fileName)

      // Restaurar botón
      button.innerHTML = originalText
      button.disabled = false
    } catch (error) {
      console.error('Error generando PDF:', error)
      alert('Error al generar el PDF. Por favor, intenta nuevamente.')
      button.innerHTML = originalText
      button.disabled = false
    }
  }

  /**
   * Renderiza el texto de título y subtítulo en un canvas a 300 DPI
   */
  async renderCoverTextToCanvas(titleText, subtitleText, positions, coverWidthPx, coverHeightPx, fontFamily) {
    try {
      await document.fonts.ready
      
      // Extraer nombre de fuente
      const fontMatch = fontFamily.match(/'([^']+)'/);
      const fontName = fontMatch ? fontMatch[1] : 'Arial';
      
      // Cargar fuente
      try {
        await Promise.all([
          document.fonts.load(`400 1em "${fontName}"`),
          document.fonts.load(`700 1em "${fontName}"`)
        ])
        await new Promise(resolve => setTimeout(resolve, 300))
      } catch (e) {
        console.warn('No se pudo cargar la fuente:', fontName, e)
      }
      
      // Crear canvas
      const canvas = document.createElement('canvas')
      canvas.width = coverWidthPx
      canvas.height = coverHeightPx
      const ctx = canvas.getContext('2d')
      
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // Dibujar título
      if (titleText && positions.title.lines.length > 0) {
        ctx.font = `bold ${positions.title.fontSize}px "${fontName}", sans-serif`
        ctx.fillStyle = '#FFFFFF'
        ctx.textAlign = positions.title.textAnchor === 'start' ? 'left' : positions.title.textAnchor === 'end' ? 'right' : 'center'
        ctx.textBaseline = 'middle'
        
        positions.title.lines.forEach((line, index) => {
          const lineY = positions.title.y + ((index - (positions.title.lines.length - 1) / 2) * positions.title.lineHeight)
          ctx.fillText(line, positions.title.x, lineY)
        })
      }
      
      // Dibujar subtítulo
      if (subtitleText && positions.subtitle.lines.length > 0) {
        ctx.font = `${positions.subtitle.fontSize}px "${fontName}", sans-serif`
        ctx.fillStyle = '#FFFFFF'
        ctx.textAlign = positions.subtitle.textAnchor === 'start' ? 'left' : positions.subtitle.textAnchor === 'end' ? 'right' : 'center'
        ctx.textBaseline = 'middle'
        
        positions.subtitle.lines.forEach((line, index) => {
          const lineY = positions.subtitle.y + ((index - (positions.subtitle.lines.length - 1) / 2) * positions.subtitle.lineHeight)
          ctx.fillText(line, positions.subtitle.x, lineY)
        })
      }
      
      return canvas.toDataURL('image/png')
    } catch (error) {
      console.error('Error renderizando texto de portada:', error)
      return null
    }
  }

  /**
   * Renderiza el texto del lomo en un canvas a 300 DPI y lo convierte a PNG
   * Esto asegura que el texto se vea exactamente como en el navegador
   */
  async renderSpineTextToCanvas(text, spineWidthPx, spineHeightPx, cover) {
    try {
      const dpi = 300
      
      // Esperar a que las fuentes se carguen completamente
      await document.fonts.ready
      
      // Obtener la tipografía seleccionada
      const selectedFont = this.fontSelect?.value || cover.font_families?.split(',')[0]?.trim() || 'Arial'
      
      // Cargar la fuente específica
      try {
        await Promise.all([
          document.fonts.load(`400 1em "${selectedFont}"`),
          document.fonts.load(`700 1em "${selectedFont}"`)
        ])
        // Esperar un poco más para asegurar que la fuente se renderice
        await new Promise(resolve => setTimeout(resolve, 300))
      } catch (e) {
        console.warn('No se pudo cargar la fuente:', selectedFont, e)
      }
      
      // Calcular tamaño de fuente (67.5% del ancho del lomo)
      const spineFontSizePx = spineWidthPx * 0.675
      
      // Crear canvas con dimensiones exactas a 300 DPI
      const canvas = document.createElement('canvas')
      canvas.width = spineWidthPx
      canvas.height = spineHeightPx
      const ctx = canvas.getContext('2d')
      
      // Configurar fondo transparente
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // Configurar fuente y estilo de texto
      ctx.font = `bold ${spineFontSizePx}px "${selectedFont}", sans-serif`
      ctx.fillStyle = '#FFFFFF'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      
      // Guardar el contexto antes de rotar
      ctx.save()
      
      // Mover al centro del canvas
      ctx.translate(spineWidthPx / 2, spineHeightPx / 2)
      
      // Rotar -90 grados (texto vertical)
      ctx.rotate(-Math.PI / 2)
      
      // Dibujar el texto centrado
      ctx.fillText(text, 0, 0)
      
      // Restaurar el contexto
      ctx.restore()
      
      // Convertir canvas a PNG base64
      return canvas.toDataURL('image/png')
    } catch (error) {
      console.error('Error renderizando texto del lomo:', error)
      return null
    }
  }

  /**
   * Carga una imagen desde una URL (blob, data URL, o URL normal) y la convierte a data URL
   * @param {string} imageUrl - URL de la imagen
   * @returns {Promise<string>} Data URL de la imagen
   */
  async loadImageAsDataUrl(imageUrl) {
    // Si ya es un data URL, retornarlo directamente
    if (imageUrl.startsWith('data:')) {
      return imageUrl
    }

    // Si es una blob URL, convertirla a data URL
    if (imageUrl.startsWith('blob:')) {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch (error) {
        console.error('Error loading blob URL:', error)
        throw error
      }
    }

    // Para URLs normales, cargar la imagen y convertirla a canvas
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous' // Permitir CORS si es necesario
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          resolve(canvas.toDataURL('image/jpeg', 0.95))
        } catch (error) {
          console.error('Error converting image to data URL:', error)
          reject(error)
        }
      }
      
      img.onerror = (error) => {
        console.error('Error loading image:', error)
        reject(error)
      }
      
      img.src = imageUrl
    })
  }

  /**
   * Convierte el SVG completo a imagen base64 para usar en el PDF
   * Esto asegura que el PDF se vea exactamente igual que el frontend
   */
  async convertSVGToImage(svgElement, widthPx, heightPx) {
    try {
      // Clonar el SVG para no modificar el original
      const clonedSvg = svgElement.cloneNode(true)
      
      // Establecer dimensiones explícitas en el SVG clonado
      clonedSvg.setAttribute('width', widthPx)
      clonedSvg.setAttribute('height', heightPx)
      clonedSvg.setAttribute('viewBox', `0 0 ${widthPx} ${heightPx}`)
      
      // Serializar el SVG a string
      const serializer = new XMLSerializer()
      const svgString = serializer.serializeToString(clonedSvg)
      
      // Crear blob y URL
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(svgBlob)
      
      // Crear imagen y canvas
      const img = new Image()
      const canvas = document.createElement('canvas')
      canvas.width = widthPx
      canvas.height = heightPx
      const ctx = canvas.getContext('2d')
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          // Dibujar la imagen en el canvas con las dimensiones exactas
          ctx.drawImage(img, 0, 0, widthPx, heightPx)
          URL.revokeObjectURL(url)
          resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = (error) => {
          URL.revokeObjectURL(url)
          reject(error)
        }
        img.src = url
      })
    } catch (error) {
      console.error('Error convirtiendo SVG a imagen:', error)
      return null
    }
  }

  /**
   * Carga y convierte el logo SVG a imagen base64 manteniendo el aspect ratio
   * @param {string} svgUrl - URL del SVG
   * @param {number} svgWidth - Ancho del viewBox del SVG
   * @param {number} svgHeight - Alto del viewBox del SVG
   */
  async loadAndConvertLogo(svgUrl, svgWidth, svgHeight) {
    try {
      const response = await fetch(svgUrl)
      const svgText = await response.text()
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(svgBlob)
      const img = new Image()
      
      return new Promise((resolve, reject) => {
        img.onload = () => {
          // Calcular dimensiones del canvas manteniendo el aspect ratio
          // Usar un tamaño base que sea lo suficientemente grande para buena calidad
          const baseSize = 1000
          let canvasWidth, canvasHeight
          
          if (svgWidth && svgHeight) {
            // Calcular dimensiones basadas en el aspect ratio del SVG
            const aspectRatio = svgWidth / svgHeight
            if (aspectRatio >= 1) {
              // Horizontal o cuadrado
              canvasWidth = baseSize
              canvasHeight = baseSize / aspectRatio
            } else {
              // Vertical
              canvasHeight = baseSize
              canvasWidth = baseSize * aspectRatio
            }
          } else {
            // Fallback: usar dimensiones naturales de la imagen
            canvasWidth = img.width || baseSize
            canvasHeight = img.height || baseSize
          }
          
          const canvas = document.createElement('canvas')
          canvas.width = canvasWidth
          canvas.height = canvasHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
          URL.revokeObjectURL(url)
          resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = reject
        img.src = url
      })
    } catch (error) {
      console.error('Error cargando logo:', error)
      return null
    }
  }
}
